"use client";

import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import * as React from "react";
import * as RechartsPrimitive from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { z } from "zod";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = Record<
    string,
    {
        label?: React.ReactNode;
        icon?: React.ComponentType;
    } & ({ color?: string; theme?: never } | { color?: never; theme: Record<keyof typeof THEMES, string> })
>;

type ChartContextProps = {
    config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
    const context = React.useContext(ChartContext);
    if (!context) {
        throw new Error("useChart must be used within a <ChartContainer />");
    }

    return context;
}

const ChartContainer = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<"div"> & {
        config: ChartConfig;
        children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
    }
>(({ id, className, children, config, ...props }, ref) => {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

    return (
        <div className="flex size-full">
            {/*
                `flex` on the div above is a fix for Safari, which does not respect the `max-h` with `h-full` in `ChartContainer`:
                https://stackoverflow.com/questions/19119910/safari-height-100-element-inside-a-max-height-element
            */}
            <ChartContext.Provider value={{ config }}>
                <div
                    data-chart={chartId}
                    ref={ref}
                    className={cn(
                        "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-content-primary [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-radial-bar-background-sector]:fill-content-secondary [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-content-secondary [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
                        className,
                    )}
                    {...props}
                >
                    <ChartStyle id={chartId} config={config} />
                    <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
                </div>
            </ChartContext.Provider>
        </div>
    );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
    const colorConfig = Object.entries(config).filter(([, config]) => config.theme || config.color);

    if (!colorConfig.length) {
        return null;
    }

    return (
        <style
            dangerouslySetInnerHTML={{
                __html: Object.entries(THEMES)
                    .map(
                        ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
    .map(([key, itemConfig]) => {
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
    })
    .join("\n")}
}
`,
                    )
                    .join("\n"),
            }}
        />
    );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const itemPayloadSchema = z.object({
    fill: z.string(),
});

// this is the bare minimum in terms of type safety for the payload data. It shouldn't be used as an exhaustive schema for the payload data (hence the passthrough)
const payloadDataSchema = z.array(
    z
        .object({
            name: z.string(),
        })
        .passthrough(),
);

type ChartTooltipContentProps = PropsWithClassName &
    React.ComponentProps<typeof RechartsPrimitive.Tooltip> & {
        hideLabel?: boolean;
        hideIndicator?: boolean;
        indicator?: "line" | "dot" | "dashed";
        nameKey?: string;
        labelKey?: string;
        color?: string;
        valueFormatter?: (value: ValueType) => string;
        /** Sort payload items before rendering. Receives the same array Recharts provides. */
        sortPayload?: (payload: NonNullable<RechartsPrimitive.TooltipProps<ValueType, NameType>["payload"]>) => NonNullable<RechartsPrimitive.TooltipProps<ValueType, NameType>["payload"]>;
        /** Show a total row summing all numeric values, separated by a border. */
        showTotal?: boolean;
        /** Label for the total row. Defaults to "Total". */
        totalLabel?: string;
    };

const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
    (
        {
            active,
            payload,
            className,
            indicator = "dot",
            hideLabel = false,
            hideIndicator = false,
            label,
            labelFormatter,
            valueFormatter,
            labelClassName,
            formatter,
            color,
            nameKey,
            labelKey,
            sortPayload,
            showTotal = false,
            totalLabel = "Total",
        },
        ref,
    ) => {
        const { config } = useChart();

        const tooltipLabel = React.useMemo(() => {
            if (hideLabel || !payload?.length) {
                return null;
            }

            const [item] = payload;
            const key = labelKey || item?.dataKey || item?.name || "value";
            const itemConfig = getPayloadConfigFromPayload(config, item, key.toString());
            const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;

            if (labelFormatter) {
                return <div className={cn("font-medium", labelClassName)}>{labelFormatter(value, payload)}</div>;
            }

            if (!value) {
                return null;
            }

            return <div className={cn("font-medium", labelClassName)}>{value}</div>;
        }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

        if (!active || !payload?.length) {
            return null;
        }

        const nestLabel = payload.length === 1 && indicator !== "dot";
        const sortedPayload = sortPayload ? sortPayload(payload) : payload;

        const total = showTotal
            ? sortedPayload.reduce((sum, item) => sum + (typeof item.value === "number" ? item.value : 0), 0)
            : 0;

        return (
            <div
                ref={ref}
                className={cn(
                    "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border-base/50 bg-surface-popover/50 px-2.5 py-1.5 text-sm text-content-primary shadow-lg backdrop-blur-xl",
                    className,
                )}
            >
                {!nestLabel ? tooltipLabel : null}
                <div className="grid gap-1.5">
                    {sortedPayload.map((item, index) => {
                        const key = nameKey || item.name || item.dataKey || "value";
                        const itemConfig = getPayloadConfigFromPayload(config, item, key.toString());

                        const payloadFillResult = itemPayloadSchema.safeParse(item.payload);
                        const indicatorColor = color || payloadFillResult.data?.fill || item.color;

                        const payloadDataResult = payloadDataSchema.safeParse(item.payload);
                        const payloadData = payloadDataResult.data;

                        return (
                            <div
                                key={item.dataKey}
                                className={cn(
                                    "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-content-secondary",
                                    { "items-center": indicator === "dot" },
                                )}
                            >
                                {formatter && item?.value !== undefined && item.name && payloadData ? (
                                    formatter(item.value, item.name, item, index, payloadData)
                                ) : (
                                    <>
                                        {itemConfig?.icon ? (
                                            <itemConfig.icon />
                                        ) : (
                                            !hideIndicator && (
                                                <div
                                                    className={cn(
                                                        "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]",
                                                        {
                                                            "h-2.5 w-2.5": indicator === "dot",
                                                            "w-1": indicator === "line",
                                                            "w-0 border-[1.5px] border-dashed bg-transparent":
                                                                indicator === "dashed",
                                                            "my-0.5": nestLabel && indicator === "dashed",
                                                        },
                                                    )}
                                                    style={
                                                        {
                                                            "--color-bg": indicatorColor,
                                                            "--color-border": indicatorColor,
                                                        } as React.CSSProperties
                                                    }
                                                />
                                            )
                                        )}
                                        <div
                                            className={cn("flex flex-1 justify-between gap-2 leading-none", {
                                                "items-end": nestLabel,
                                                "items-center": !nestLabel,
                                            })}
                                        >
                                            <div className="grid gap-1.5">
                                                {nestLabel ? tooltipLabel : null}
                                                <span className="text-content-primary">
                                                    {itemConfig?.label || item.name}
                                                </span>
                                            </div>
                                            {item.value !== undefined && (
                                                <span className="font-mono font-medium tabular-nums text-content-primary">
                                                    {valueFormatter
                                                        ? valueFormatter(item.value)
                                                        : item.value.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
                {showTotal && total > 0 && (
                    <div className="flex items-center justify-between gap-8 border-t border-border-light pt-1.5">
                        <span className="font-medium text-content-primary">{totalLabel}</span>
                        <span className="font-mono font-medium tabular-nums text-content-primary">
                            {valueFormatter ? valueFormatter(total) : Math.round(total).toLocaleString()}
                        </span>
                    </div>
                )}
            </div>
        );
    },
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<"div"> &
        Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
            hideIcon?: boolean;
            nameKey?: string;
        }
>(({ className, hideIcon = false, payload, verticalAlign = "bottom", nameKey }, ref) => {
    const { config } = useChart();

    if (!payload?.length) {
        return null;
    }

    return (
        <div
            ref={ref}
            className={cn(
                "flex items-center justify-center gap-4",
                { "pb-3": verticalAlign === "top", "pt-3": verticalAlign !== "top" },
                className,
            )}
        >
            {payload.map((item) => {
                const key = nameKey || item.dataKey || "value";
                const itemConfig = getPayloadConfigFromPayload(config, item, key.toString());

                return (
                    <div
                        key={item.value as string}
                        className={cn(
                            "flex items-center gap-1.5 text-base [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-content-secondary",
                        )}
                    >
                        {itemConfig?.icon && !hideIcon ? (
                            <itemConfig.icon />
                        ) : (
                            <div
                                className="size-[14px] shrink-0 rounded-[2px]"
                                style={{
                                    backgroundColor: item.color,
                                }}
                            />
                        )}
                        {itemConfig?.label}
                    </div>
                );
            })}
        </div>
    );
});
ChartLegendContent.displayName = "ChartLegend";

const payloadSchema = z
    .object({
        payload: z.record(z.unknown()).optional(),
    })
    .passthrough();

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
    const result = payloadSchema.safeParse(payload);
    if (!result.success) {
        return undefined;
    }

    const { data } = result;
    const { payload: payloadPayload } = data;

    let configLabelKey: string = key;

    if (key in data) {
        const value = data[key];
        if (typeof value === "string") {
            configLabelKey = value;
        }
    } else if (payloadPayload && key in payloadPayload) {
        const value = payloadPayload[key];
        if (typeof value === "string") {
            configLabelKey = value;
        }
    }

    return configLabelKey in config ? config[configLabelKey] : config[key];
}

export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, ChartStyle };
